import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  User, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  Bot,
  ArrowLeft,
  CheckCircle,
  Languages,
  Gift
} from "lucide-react";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Globe, ChevronDown, Check } from "lucide-react";
import { 
  saveReferralCode, 
  getReferralCode, 
  getReferralData 
} from "@/utils/referralTracking";
import { safeLocalStorage } from "@/utils/safeStorage";

const API = process.env.REACT_APP_BACKEND_URL;

// Professional Floating Language Toggle
const FloatingLanguageToggle = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  
  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ];
  const currentLang = languages.find(l => l.code === language) || languages[0];
  
  return (
    <div className="fixed top-4 right-4 z-50">
      <button onClick={() => setIsOpen(!isOpen)} data-testid="auth-language-toggle"
        className="flex items-center gap-2 px-3 py-2 rounded-lg glass border border-white/20 hover:border-white/40">
        <Globe size={16} className="text-cyan-400" />
        <span className="text-sm font-medium text-white">{currentLang.flag} {currentLang.code.toUpperCase()}</span>
        <ChevronDown size={14} className={`text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full mt-2 right-0 z-50 min-w-[150px] py-1 rounded-xl glass border border-white/20">
            {languages.map((lang) => (
              <button key={lang.code} onClick={() => { setLanguage(lang.code); setIsOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 ${language === lang.code ? 'text-cyan-400' : 'text-white'}`}>
                <span>{lang.flag}</span><span className="text-sm">{lang.name}</span>
                {language === lang.code && <Check size={14} className="ml-auto" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default function Register() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [checkingReferral, setCheckingReferral] = useState(false);
  
  const { register } = useAuth();
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();

  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralValid(null);
      return;
    }
    
    setCheckingReferral(true);
    try {
      const res = await fetch(`${API}/api/referral/validate/${code.toUpperCase()}`);
      const data = await res.json();
      setReferralValid(data.valid);
      
      // If valid, save with multi-layer persistence
      if (data.valid) {
        saveReferralCode(code);
      }
    } catch (error) {
      setReferralValid(false);
    }
    setCheckingReferral(false);
  };

  // Check for referral code on mount - from URL or saved tracking
  useEffect(() => {
    // Priority 1: Check URL parameter
    const refCode = searchParams.get('ref');
    if (refCode) {
      const upperCode = refCode.toUpperCase();
      setFormData(prev => ({ ...prev, referralCode: upperCode }));
      saveReferralCode(upperCode); // Save with multi-layer persistence
      validateReferralCode(upperCode);
      return;
    }
    
    // Priority 2: Check for previously saved referral (cookie/localStorage)
    const savedCode = getReferralCode();
    if (savedCode) {
      setFormData(prev => ({ ...prev, referralCode: savedCode }));
      validateReferralCode(savedCode);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error(t('errors.required'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error(t('errors.passwordMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t('errors.minLength', { length: 6 }));
      return;
    }

    if (!agreed) {
      toast.error(t('errors.required'));
      return;
    }

    setLoading(true);
    
    // Save referral code with device fingerprint before registration
    if (formData.referralCode && referralValid) {
      const referralData = getReferralData();
      // Store complete referral data for tracking after OTP
      safeLocalStorage.setItem('pending_referral_data', JSON.stringify({
        code: formData.referralCode,
        fingerprint: referralData.fingerprint,
        timestamp: Date.now()
      }));
    }
    
    const result = await register(formData.name, formData.email, formData.password);
    
    if (result.success) {
      toast.success(t('success.created'));
      setLoading(false);
      navigate('/login');
    } else {
      safeLocalStorage.removeItem('pending_referral_data');
      setLoading(false);
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="register-page">
      {/* Floating Language Toggle */}
      <FloatingLanguageToggle />
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center mx-auto mb-4 neon-glow">
            <Bot size={32} className="text-black" />
          </div>
          <h1 className="text-2xl font-bold">{t('auth.createAccount')}</h1>
          <p className="text-neutral-500 mt-2">{t('auth.registerDescription')}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="glass rounded-2xl p-8 space-y-6">
          {/* Name */}
          <div>
            <label className="text-sm text-neutral-400 block mb-2">{t('auth.name')}</label>
            <div className="relative">
              <User className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-neutral-500`} size={18} />
              <Input
                data-testid="register-name"
                type="text"
                placeholder={t('auth.name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={isRTL ? 'pr-10' : 'pl-10'}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="text-sm text-neutral-400 block mb-2">{t('auth.email')}</label>
            <div className="relative">
              <Mail className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-neutral-500`} size={18} />
              <Input
                data-testid="register-email"
                type="email"
                placeholder="example@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={isRTL ? 'pr-10' : 'pl-10'}
                dir="ltr"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="text-sm text-neutral-400 block mb-2">{t('auth.password')}</label>
            <div className="relative">
              <Lock className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-neutral-500`} size={18} />
              <Input
                data-testid="register-password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className={isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'}
                dir="ltr"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white`}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="text-sm text-neutral-400 block mb-2">{t('auth.confirmPassword')}</label>
            <div className="relative">
              <Lock className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-neutral-500`} size={18} />
              <Input
                data-testid="register-confirm-password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className={isRTL ? 'pr-10' : 'pl-10'}
                dir="ltr"
              />
            </div>
          </div>

          {/* Referral Code */}
          <div>
            <label className="text-sm text-neutral-400 block mb-2">
              {isRTL ? 'كود الإحالة (اختياري)' : 'Referral Code (Optional)'}
            </label>
            <div className="relative">
              <Gift className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-neutral-500`} size={18} />
              <Input
                data-testid="register-referral-code"
                type="text"
                placeholder={isRTL ? 'أدخل كود الإحالة' : 'Enter referral code'}
                value={formData.referralCode}
                onChange={(e) => {
                  const code = e.target.value.toUpperCase();
                  setFormData({ ...formData, referralCode: code });
                  if (code.length >= 4) {
                    validateReferralCode(code);
                  } else {
                    setReferralValid(null);
                  }
                }}
                className={`${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} ${
                  referralValid === true ? 'border-green-500 focus:border-green-500' : 
                  referralValid === false ? 'border-red-500 focus:border-red-500' : ''
                }`}
                dir="ltr"
              />
              {/* Validation indicator */}
              <div className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2`}>
                {checkingReferral && (
                  <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                )}
                {!checkingReferral && referralValid === true && (
                  <CheckCircle size={18} className="text-green-500" />
                )}
                {!checkingReferral && referralValid === false && (
                  <span className="text-red-500 text-xs">{isRTL ? 'غير صالح' : 'Invalid'}</span>
                )}
              </div>
            </div>
            {referralValid === true && (
              <p className="text-xs text-green-500 mt-1">
                {isRTL ? '✓ كود الإحالة صالح!' : '✓ Valid referral code!'}
              </p>
            )}
          </div>

          {/* Terms */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="terms"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1"
            />
            <label htmlFor="terms" className="text-sm text-neutral-400">
              {t('common.confirm')}{' '}
              <Link to="/terms" className="text-cyan-400 hover:underline">{isRTL ? 'الشروط والأحكام' : 'Terms'}</Link>
              {' '}&{' '}
              <Link to="/privacy" className="text-cyan-400 hover:underline">{isRTL ? 'سياسة الخصوصية' : 'Privacy Policy'}</Link>
            </label>
          </div>

          {/* Submit */}
          <Button
            data-testid="register-submit"
            type="submit"
            className="w-full btn-primary"
            disabled={loading}
          >
            {loading ? t('common.loading') : t('auth.signUp')}
          </Button>

          {/* Login Link */}
          <p className="text-center text-sm text-neutral-500">
            {t('auth.hasAccount')}{' '}
            <Link to="/login" className="text-cyan-400 hover:underline">
              {t('auth.signIn')}
            </Link>
          </p>
        </form>

        {/* Features */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center">
          <div className="p-3">
            <CheckCircle className="mx-auto text-green-400 mb-2" size={20} />
            <p className="text-xs text-neutral-500">{isRTL ? 'شهر تجريبي مجاني' : 'Free Trial'}</p>
          </div>
          <div className="p-3">
            <CheckCircle className="mx-auto text-green-400 mb-2" size={20} />
            <p className="text-xs text-neutral-500">{isRTL ? 'بدون بطاقة ائتمان' : 'No Credit Card'}</p>
          </div>
          <div className="p-3">
            <CheckCircle className="mx-auto text-green-400 mb-2" size={20} />
            <p className="text-xs text-neutral-500">{isRTL ? 'إلغاء في أي وقت' : 'Cancel Anytime'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
